import { useCallback, useEffect, useRef } from 'react';

import { getLogger } from '@packages/core/logging';

const logger = getLogger('web');

// Keep the gap at 5 minutes: IdleWarningPopup counts down from its own hardcoded
// 5 minutes and fires the logout itself when it hits zero.
const WARNING_TIMEOUT_MS = 475 * 60 * 1000; // 7 hours 55 minutes - show warning popup
const IDLE_TIMEOUT_MS = 480 * 60 * 1000; // 8 hours - auto logout
const RESET_THROTTLE_MS = 5_000;

const WINDOW_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'touchstart'] as const;
const DOCUMENT_EVENTS = ['visibilitychange'] as const;

export interface UseIdleTimeoutOptions {
    onWarning: () => void;
    onTimeout: () => void | Promise<void>;
    onActive?: () => void;
    activityStorageKey?: string;
    enabled: boolean;
}

function readSharedActivity(key: string | undefined) {
    if (!key) {
        return 0;
    }

    return Number(window.localStorage.getItem(key)) || 0;
}

function writeSharedActivity(key: string | undefined, at: number) {
    if (!key) {
        return;
    }

    window.localStorage.setItem(key, String(at));
}

export function useIdleTimeout({
    onWarning,
    onTimeout,
    onActive,
    activityStorageKey,
    enabled,
}: UseIdleTimeoutOptions): { reset: () => void } {
    const warningTimerRef = useRef<number | undefined>(undefined);
    const logoutTimerRef = useRef<number | undefined>(undefined);
    const throttleRef = useRef<number | undefined>(undefined);
    const lastActivityRef = useRef(0);
    const onWarningRef = useRef(onWarning);
    const onTimeoutRef = useRef(onTimeout);
    const onActiveRef = useRef(onActive);
    const activityStorageKeyRef = useRef(activityStorageKey);
    onWarningRef.current = onWarning;
    onTimeoutRef.current = onTimeout;
    onActiveRef.current = onActive;
    activityStorageKeyRef.current = activityStorageKey;

    const startTimers = useCallback((from: number = Date.now()) => {
        if (warningTimerRef.current !== undefined) {
            clearTimeout(warningTimerRef.current);
        }
        if (logoutTimerRef.current !== undefined) {
            clearTimeout(logoutTimerRef.current);
        }

        lastActivityRef.current = from;
        const idleForMs = Date.now() - from;

        const deferToOtherTabs = () => {
            const sharedActivity = readSharedActivity(activityStorageKeyRef.current);
            if (sharedActivity <= lastActivityRef.current) {
                return false;
            }

            startTimers(sharedActivity);
            onActiveRef.current?.();
            return true;
        };

        warningTimerRef.current = window.setTimeout(
            () => {
                if (deferToOtherTabs()) {
                    return;
                }
                onWarningRef.current();
            },
            Math.max(0, WARNING_TIMEOUT_MS - idleForMs)
        );

        logoutTimerRef.current = window.setTimeout(
            () => {
                if (deferToOtherTabs()) {
                    return;
                }
                Promise.resolve(onTimeoutRef.current()).catch((err) => {
                    logger.error('The idle timeout onTimeout handler threw an unhandled error', { error: err });
                });
            },
            Math.max(0, IDLE_TIMEOUT_MS - idleForMs)
        );
    }, []);

    const clearAllTimers = useCallback(() => {
        if (warningTimerRef.current !== undefined) {
            clearTimeout(warningTimerRef.current);
            warningTimerRef.current = undefined;
        }
        if (logoutTimerRef.current !== undefined) {
            clearTimeout(logoutTimerRef.current);
            logoutTimerRef.current = undefined;
        }
        if (throttleRef.current !== undefined) {
            clearTimeout(throttleRef.current);
            throttleRef.current = undefined;
        }
    }, []);

    const markActive = useCallback(() => {
        const now = Date.now();
        startTimers(now);
        writeSharedActivity(activityStorageKeyRef.current, now);
    }, [startTimers]);

    const handleActivity = useCallback(() => {
        if (throttleRef.current !== undefined) {
            return;
        }
        markActive();
        throttleRef.current = window.setTimeout(() => {
            throttleRef.current = undefined;
        }, RESET_THROTTLE_MS);
    }, [markActive]);

    const reset = useCallback(() => {
        markActive();
    }, [markActive]);

    useEffect(() => {
        if (!enabled) {
            clearAllTimers();
            return;
        }

        startTimers();

        const handleStorage = (event: StorageEvent) => {
            if (!activityStorageKeyRef.current || event.key !== activityStorageKeyRef.current) {
                return;
            }

            const sharedActivity = Number(event.newValue) || 0;
            if (sharedActivity <= lastActivityRef.current) {
                return;
            }

            startTimers(sharedActivity);
            onActiveRef.current?.();
        };

        WINDOW_EVENTS.forEach((event) => {
            window.addEventListener(event, handleActivity, { passive: true });
        });
        DOCUMENT_EVENTS.forEach((event) => {
            document.addEventListener(event, handleActivity, { passive: true });
        });
        window.addEventListener('storage', handleStorage);

        return () => {
            clearAllTimers();
            WINDOW_EVENTS.forEach((event) => {
                window.removeEventListener(event, handleActivity);
            });
            DOCUMENT_EVENTS.forEach((event) => {
                document.removeEventListener(event, handleActivity);
            });
            window.removeEventListener('storage', handleStorage);
        };
    }, [enabled, handleActivity, startTimers, clearAllTimers]);

    return { reset };
}
